import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import BlendedService from "../Service/BlendedService";

/**
 * BlendedCourseManagementPage component to manage and display blended course information.
 * @returns {JSX.Element} The BlendedCourseManagementPage component.
 */
function BlendedCourseManagementPage() {
  const location = useLocation();
  const { user } = location.state || {}; // Extract user from location.state
  const [blendedCourses, setBlendedCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBlendedCourses();
  }, []);

  /**
   * Fetches all blended courses from the server.
   */
  const fetchBlendedCourses = async () => {
    try {
      setLoading(true);
      const response = await BlendedService.getAllBlendedCourses(user.track);
      setBlendedCourses(response);
    } catch (error) {
      console.error("Error fetching Blended Courses:", error);
      alert("Failed to load blended courses: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deletes a blended course by ID.
   * @param {string} blendedProgramId - The ID of the course to delete.
   */
  const deleteBlendedCourse = async (blendedProgramId, blendedProgramName) => {
    try {
      const confirmDelete = window.confirm(`Are you sure you want to delete the blended course "${blendedProgramName}"?`);
      if (confirmDelete) {
        const result = await BlendedService.deleteCourse(blendedProgramId, blendedProgramName);
        
        if (result) {
          setBlendedCourses(blendedCourses.filter((course) => course.blendedProgramId !== blendedProgramId));
          alert("Blended course deleted successfully!");
        } else {
          alert("Cannot delete this course as it is referenced in blended learning offering.");
        }
      }
    } catch (error) {
      console.error("Error deleting blended course:", error);
      alert("Failed to delete blended course: " + error.message);
    }
  };

  return (
    <div>
      <br/>
      <h2 className="text-2xl w-1/3 mx-auto font-semibold mb-4 text-center">Blended Courses Management Page</h2>
      <br/>
      <div className="mb-4 text-center">
        <button className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 mr-3">
          <Link 
            to={{
              pathname: "/admin/add-blended-course",
              state: { user } 
            }}
            className="text-white no-underline"
          >
            Add Blended Course
          </Link>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading blended courses...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-11/12 mx-auto bg-white border border-gray-300">
            <thead>
              <tr>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  S.No
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Program Name
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Program WD Course Code
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Track
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Total Modules
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Total Duration (hrs)
                </th>
                <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                  Total VILT Connects
                </th>
                <th className="py-1 px-1 bg-gray-200 text-center text-gray-600 font-bold border border-gray-300" colSpan="2">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {blendedCourses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-6 px-4 text-center text-gray-500 border border-gray-300">
                    No blended courses found for your track
                  </td>
                </tr>
              ) : (
                blendedCourses.map((course, index) => {
                  // Calculate totals from modules
                  const totalModules = course.modules ? course.modules.length : 0;
                  const totalDuration = course.modules ? 
                    course.modules.reduce((sum, module) => sum + (module.duration || 0), 0) : 0;
                  const totalSMEConnects = course.modules ? 
                    course.modules.reduce((sum, module) => sum + (module.numOfSMEConnects || 0), 0) : 0;

                  return (
                    <tr key={course.blendedProgramId} className="hover:bg-gray-50">
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">{index + 1}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm font-medium">{course.blendedProgramName}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm">{course.programWDCourseCode}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm">{course.track}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">{totalModules}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">{totalDuration}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">{totalSMEConnects}</td>
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">
                        <button 
                          className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors" 
                          onClick={() => deleteBlendedCourse(course.blendedProgramId, course.blendedProgramName)}
                        >
                          Delete
                        </button>
                      </td>
                      <td className="py-3 px-4 border border-gray-300 text-sm text-center">
                        <button className="bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors">
                          <Link
                            to={{
                              pathname: `/admin/update-blended-course/${course.blendedProgramId}`,
                              state: { user } 
                            }}
                            className="text-white no-underline"
                          >
                            Update
                          </Link>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      <br/>
    </div>
  );
}

export default BlendedCourseManagementPage;